import { useState } from 'react';
import { 
  Activity, 
  ArrowRight, 
  CheckCircle2, 
  Heart, 
  Pill, 
  ShieldCheck, 
  Stethoscope, 
  Thermometer, 
  Zap, 
  ChevronLeft, 
  Flame, 
  AlertCircle,
  Plus,
  Edit3,
  X,
} from 'lucide-react';
import { Button } from '@medikei/ui';

export type RiskLevel = 'EMERGENCY' | 'HIGH' | 'MODERATE' | 'LOW';

export interface TriageResult {
  riskClassification: RiskLevel;
  redFlags: string[];
  recommendedSpecialty?: string;
  summary: string;
  requiresImmediateCare: boolean;
  chiefComplaint: string;
  symptomIntensity: number;
  symptomDuration: string;
  relevantHistory: string[];
  currentMedications: string;
  triageCompleted?: boolean;
  failureReason?: string;
}

interface TriageQuestionnaireProps {
  patientId?: string;
  appointmentId?: string;
  onComplete?: (result: TriageResult) => void;
  onCancel?: () => void;
}

// Categorias Clínicas Pré-Definidas (Zero margem para burlar com gírias ou textos desconexos)
const PRIMARY_COMPLAINTS = [
  { id: 'dor_cabeca', label: 'Dor de cabeça / Enxaqueca', icon: Activity, category: 'Neurológico', specialty: 'Clínica Geral' },
  { id: 'febre_gripe', label: 'Febre / Gripe / Resfriado', icon: Thermometer, category: 'Infeccioso', specialty: 'Clínica Geral' },
  { id: 'dor_estomago', label: 'Dor de estômago / Náusea / Azia', icon: Flame, category: 'Digestivo', specialty: 'Gastroenterologia' },
  { id: 'dor_garganta', label: 'Dor de garganta / Tosse', icon: Activity, category: 'Respiratório', specialty: 'Clínica Geral' },
  { id: 'dor_corpo', label: 'Dores musculares / Nas costas', icon: Activity, category: 'Ortopédico', specialty: 'Ortopedia' },
  { id: 'dor_peito', label: 'Aperto / Dor no peito', icon: Heart, category: 'Cardiovascular', specialty: 'Pronto Socorro', isEmergency: true },
  { id: 'falta_ar', label: 'Falta de ar / Dificuldade respiratória', icon: Zap, category: 'Respiratório Grave', specialty: 'Pronto Socorro', isEmergency: true },
  { id: 'alergia_pele', label: 'Alergia / Manchas na pele / Coceira', icon: ShieldCheck, category: 'Dermatológico', specialty: 'Dermatologia' },
  { id: 'ansiedade', label: 'Crise de ansiedade / Pânico', icon: Heart, category: 'Saúde Mental', specialty: 'Psicologia' },
  { id: 'outro', label: 'Outro sintoma clínico geral', icon: Stethoscope, category: 'Geral', specialty: 'Clínica Geral' },
];

const SECONDARY_SYMPTOMS_BY_COMPLAINT: Record<string, string[]> = {
  dor_cabeca: [
    'Sensibilidade à luz (fotofobia)',
    'Sensibilidade ao barulho (fonofobia)',
    'Náusea ou vômito',
    'Visão embaçada ou pontos brilhantes (aura)',
    'Tontura ou vertigem',
    'Dor ou rigidez na nuca / pescoço',
    'Dor pulsátil em um lado da cabeça',
  ],
  febre_gripe: [
    'Coriza ou nariz entupido',
    'Dor de garganta ao engolir',
    'Tosse seca ou com secreção',
    'Dores no corpo e articulações',
    'Calafrios ou tremores',
    'Fadiga ou fraqueza intensa',
    'Dor de cabeça',
  ],
  dor_estomago: [
    'Azia ou queimação no esôfago',
    'Náusea ou vômito',
    'Refluxo de líquido ácido',
    'Sensação de estufamento após comer',
    'Diarreia ou fezes amolecidas',
    'Cólicas abdominais',
    'Perda de apetite ou mal-estar',
  ],
  dor_garganta: [
    'Dificuldade ou dor intensa para engolir',
    'Tosse seca ou com catarro',
    'Sensação de garganta arranhando',
    'Febre medida com termômetro',
    'Rouquidão ou perda de voz',
    'Gânglios doloridos no pescoço (íngua)',
    'Mau hálito ou pus nas amígdalas',
  ],
  dor_corpo: [
    'Dor lombar (parte inferior das costas)',
    'Dor irradiando para pernas ou glúteos',
    'Rigidez ao acordar ou se movimentar',
    'Formigamento ou dormência',
    'Fraqueza em braços ou pernas',
    'Dor que piora ao sentar ou levantar',
  ],
  dor_peito: [
    'Dor irradiando para braço esquerdo, costas ou queixo',
    'Falta de ar ou respiração ofegante',
    'Suor frio e palidez repentina',
    'Palpitações ou coração acelerado',
    'Tontura ou sensação de desmaio',
    'Sensação de aperto ou peso no tórax',
  ],
  falta_ar: [
    'Chiado ou assobio no peito ao respirar',
    'Tosse com falta de ar ao deitar',
    'Dificuldade para completar frases',
    'Sensação de aperto ou sufocamento',
    'Lábios ou pontas dos dedos azulados',
    'Cansaço aos mínimos esforços',
  ],
  alergia_pele: [
    'Coceira intensa no corpo ou localizada',
    'Placas avermelhadas ou empolação (urticária)',
    'Inchaço nos lábios, olhos ou rosto',
    'Sensação de queimação ou calor na pele',
    'Descamação ou pele muito ressecada',
    'Contato recente com produto novo ou alimento diferente',
  ],
  ansiedade: [
    'Taquicardia ou palpitações cardíacas',
    'Sensação de nó ou aperto na garganta',
    'Tremores nas mãos ou pernas',
    'Suor frio nas mãos ou pés',
    'Sensação de desmaio ou tontura',
    'Medo intenso ou agitação motora',
  ],
  outro: [
    'Cansaço ou fadiga excessiva',
    'Tontura ou vertigem',
    'Alteração no apetite ou peso',
    'Dor ou desconforto ao urinar',
    'Insônia ou dificuldade para dormir',
    'Inchaço em pernas ou pés',
  ],
};

const DEFAULT_SECONDARY_SYMPTOMS = [
  'Náusea ou vômito',
  'Febre medida com termômetro',
  'Tontura ou vertigem',
  'Diarreia',
  'Coriza ou congestão nasal',
  'Fadiga ou fraqueza intensa',
  'Tosse persistente',
  'Calafrios',
];

const DURATION_OPTIONS = [
  { id: 'hoje', label: 'Começou hoje (Menos de 24 horas)', sub: 'Início agudo e recente' },
  { id: 'alguns_dias', label: 'Há alguns dias (2 a 6 dias)', sub: 'Evolução em curso' },
  { id: 'semanas', label: 'Há 1 a 3 semanas', sub: 'Subagudo' },
  { id: 'meses', label: 'Mais de 1 mês', sub: 'Quadro crônico ou recorrente' },
];

const INTENSITY_LEVELS = [
  { val: 1, label: 'Muito Leve', desc: 'Incômodo quase imperceptível' },
  { val: 3, label: 'Leve', desc: 'Não atrapalha as tarefas' },
  { val: 5, label: 'Moderada', desc: 'Incomoda e limita parcialmente' },
  { val: 8, label: 'Forte', desc: 'Dificulta muito as atividades normais' },
  { val: 10, label: 'Intensa / Insuportável', desc: 'Dor extrema ou incapacitante' },
];

export function TriageChatbot({ patientId: _patientId, appointmentId: _appointmentId, onComplete, onCancel }: TriageQuestionnaireProps) {
  // Passos do Questionário Estruturado
  // 1: Motivo Principal
  // 2: Sintomas Associados
  // 3: Duração e Intensidade
  // 4: Remédios / Alergias
  // 5: Resumo Clínico Final
  const [step, setStep] = useState<number>(1);
  const [selectedComplaint, setSelectedComplaint] = useState<typeof PRIMARY_COMPLAINTS[0] | null>(null);
  const [selectedSecondary, setSelectedSecondary] = useState<string[]>([]);
  const [customSymptomInput, setCustomSymptomInput] = useState<string>('');
  const [isCustomInputOpen, setIsCustomInputOpen] = useState<boolean>(false);
  const [selectedDuration, setSelectedDuration] = useState<string>('');
  const [intensity, setIntensity] = useState<number>(5);
  const [takesMedication, setTakesMedication] = useState<'sim' | 'nao' | null>(null);
  const [medicationDetails, setMedicationDetails] = useState<string>('');
  const [hasAllergy, setHasAllergy] = useState<'sim' | 'nao' | null>(null);
  const [allergyDetails, setAllergyDetails] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [finalResult, setFinalResult] = useState<TriageResult | null>(null);

  const handleToggleSecondary = (symptom: string) => {
    if (symptom === 'Nenhum outro sintoma') {
      setSelectedSecondary(['Nenhum outro sintoma']);
      setIsCustomInputOpen(false);
      setCustomSymptomInput('');
      return;
    }
    setSelectedSecondary((prev) => {
      const filtered = prev.filter((s) => s !== 'Nenhum outro sintoma');
      if (filtered.includes(symptom)) {
        return filtered.filter((s) => s !== symptom);
      }
      return [...filtered, symptom];
    });
  };

  const handleAddCustomSymptom = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = customSymptomInput.trim();
    if (!trimmed) return;

    setSelectedSecondary((prev) => {
      const filtered = prev.filter((s) => s !== 'Nenhum outro sintoma');
      if (!filtered.includes(trimmed)) {
        return [...filtered, trimmed];
      }
      return filtered;
    });
    setCustomSymptomInput('');
  };

  const handleRemoveCustomSymptom = (symptom: string) => {
    setSelectedSecondary((prev) => prev.filter((s) => s !== symptom));
  };

  const handleFinishQuestionnaire = async () => {
    if (!selectedComplaint) return;

    setIsSubmitting(true);

    const isEmergency = Boolean(selectedComplaint.isEmergency) || intensity >= 9;
    const redFlags: string[] = [];
    if (selectedComplaint.isEmergency) {
      redFlags.push(selectedComplaint.label);
    }
    if (intensity >= 9) {
      redFlags.push('Dor em intensidade máxima (grau 9 a 10)');
    }

    const secondaryText = selectedSecondary.length > 0 && !selectedSecondary.includes('Nenhum outro sintoma')
      ? selectedSecondary.join(', ')
      : 'Nenhum outro sintoma relatado';

    const medsText = takesMedication === 'sim' && medicationDetails.trim()
      ? medicationDetails.trim()
      : takesMedication === 'sim' ? 'Usa medicamentos contínuos (não especificados)' : 'Nenhum medicamento contínuo';

    const allergyText = hasAllergy === 'sim' && allergyDetails.trim()
      ? allergyDetails.trim()
      : hasAllergy === 'sim' ? 'Possui alergias (não especificadas)' : 'Nenhuma alergia conhecida';

    const durationLabel = DURATION_OPTIONS.find((d) => d.id === selectedDuration)?.label || 'Não informada';

    const summary = `Paciente com ${selectedComplaint.label} (${durationLabel}), intensidade ${intensity}/10. Sintomas associados: ${secondaryText}. Medicamentos: ${medsText}. Alergias: ${allergyText}.`;

    const triageData: TriageResult = {
      riskClassification: isEmergency ? 'EMERGENCY' : intensity >= 7 ? 'HIGH' : intensity >= 4 ? 'MODERATE' : 'LOW',
      redFlags,
      recommendedSpecialty: isEmergency ? 'Pronto Socorro' : selectedComplaint.specialty,
      summary,
      requiresImmediateCare: isEmergency,
      chiefComplaint: selectedComplaint.label,
      symptomIntensity: intensity,
      symptomDuration: durationLabel,
      relevantHistory: [],
      currentMedications: medsText,
      triageCompleted: true,
    };

    setFinalResult(triageData);
    setIsSubmitting(false);
    setStep(5);
    onComplete?.(triageData);
  };

  return (
    <div className="rounded-2xl sm:rounded-3xl bg-white border border-neutral-200 shadow-md flex flex-col min-h-[500px] max-h-[85vh] sm:h-[600px] overflow-hidden max-w-2xl mx-auto">
      {/* Header com Progresso Clínico */}
      <div className="flex items-center justify-between p-4 border-b bg-slate-50/80">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shadow-xs">
            <Stethoscope className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-sm text-foreground">Triagem Médica Estruturada</h3>
            <p className="text-xs text-muted-foreground">Classificação clínica Manchester validada</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {step > 1 && step < 5 && (
            <button
              type="button"
              onClick={() => setStep((prev) => Math.max(1, prev - 1))}
              className="p-1.5 text-xs text-slate-500 hover:text-slate-900 rounded-lg border border-slate-200 bg-white flex items-center gap-1"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              <span>Voltar</span>
            </button>
          )}
          <span className="text-xs font-bold px-3 py-1 bg-blue-100 text-blue-800 rounded-full border border-blue-200">
            {step === 5 ? 'Concluído' : `Etapa ${step} de 4`}
          </span>
        </div>
      </div>

      {/* Conteúdo Dinâmico do Questionário */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
        {/* ETAPA 1: Queixa Principal (Selecione o sintoma primário) */}
        {step === 1 && (
          <div className="space-y-3.5 animate-in fade-in duration-200">
            <div>
              <h4 className="text-base font-bold text-slate-900">Qual é o motivo principal da sua consulta?</h4>
              <p className="text-xs text-muted-foreground mt-0.5">
                Selecione o sintoma predominante para que o plantonista compreenda seu caso imediatamente:
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[380px] overflow-y-auto pr-1">
              {PRIMARY_COMPLAINTS.map((item) => {
                const Icon = item.icon;
                const isSelected = selectedComplaint?.id === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedComplaint(item)}
                    className={`p-3 rounded-xl border text-left flex items-start gap-3 transition-all cursor-pointer ${
                      isSelected
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20 shadow-xs'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/80 bg-white'
                    }`}
                  >
                    <div className={`p-2 rounded-lg shrink-0 ${item.isEmergency ? 'bg-rose-100 text-rose-700' : 'bg-blue-50 text-blue-700'}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-slate-900 leading-snug">{item.label}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">{item.category}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ETAPA 2: Sintomas Secundários Associados (Personalizados e Coerentes) */}
        {step === 2 && (
          <div className="space-y-3.5 animate-in fade-in duration-200">
            <div>
              <h4 className="text-base font-bold text-slate-900">Você está sentindo algum outro sintoma associado?</h4>
              <p className="text-xs text-muted-foreground mt-0.5">
                {selectedComplaint ? (
                  <span>Sintomas comumente associados a <strong>{selectedComplaint.label}</strong>:</span>
                ) : (
                  <span>Marque todos que se aplicam ao seu caso (ou selecione &quot;Nenhum outro sintoma&quot;):</span>
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {/* Opção: Nenhum outro sintoma */}
              <button
                type="button"
                onClick={() => handleToggleSecondary('Nenhum outro sintoma')}
                className={`p-3 rounded-xl border text-left flex items-center justify-between transition-all cursor-pointer ${
                  selectedSecondary.includes('Nenhum outro sintoma')
                    ? 'border-primary bg-primary/5 text-primary font-bold shadow-xs'
                    : 'border-slate-200 hover:bg-slate-50 text-slate-800'
                }`}
              >
                <span className="text-xs font-semibold">Nenhum outro sintoma</span>
                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selectedSecondary.includes('Nenhum outro sintoma') ? 'bg-primary border-primary text-white' : 'border-slate-300'}`}>
                  {selectedSecondary.includes('Nenhum outro sintoma') && <span className="text-[10px]">✓</span>}
                </div>
              </button>

              {/* Opções Personalizadas para a Queixa Principal Selecionada */}
              {((selectedComplaint?.id && SECONDARY_SYMPTOMS_BY_COMPLAINT[selectedComplaint.id]) || DEFAULT_SECONDARY_SYMPTOMS).map((symptom) => {
                const isSelected = selectedSecondary.includes(symptom);
                return (
                  <button
                    key={symptom}
                    type="button"
                    onClick={() => handleToggleSecondary(symptom)}
                    className={`p-3 rounded-xl border text-left flex items-center justify-between transition-all cursor-pointer ${
                      isSelected
                        ? 'border-primary bg-primary/5 text-primary font-bold shadow-xs'
                        : 'border-slate-200 hover:bg-slate-50 text-slate-800'
                    }`}
                  >
                    <span className="text-xs">{symptom}</span>
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${isSelected ? 'bg-primary border-primary text-white' : 'border-slate-300'}`}>
                      {isSelected && <span className="text-[10px]">✓</span>}
                    </div>
                  </button>
                );
              })}

              {/* Opção Interativa: Outro sintoma (digitar) */}
              <button
                type="button"
                onClick={() => {
                  setIsCustomInputOpen((prev) => !prev);
                  setSelectedSecondary((prev) => prev.filter((s) => s !== 'Nenhum outro sintoma'));
                }}
                className={`p-3 rounded-xl border text-left flex items-center justify-between transition-all cursor-pointer ${
                  isCustomInputOpen || selectedSecondary.some((s) => !['Nenhum outro sintoma', ...((selectedComplaint?.id && SECONDARY_SYMPTOMS_BY_COMPLAINT[selectedComplaint.id]) || DEFAULT_SECONDARY_SYMPTOMS)].includes(s))
                    ? 'border-blue-400 bg-blue-50/60 text-blue-800 font-bold'
                    : 'border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-700'
                }`}
              >
                <span className="text-xs flex items-center gap-1.5">
                  <Edit3 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                  <span>Outro sintoma (digitar)</span>
                </span>
                <Plus className="w-4 h-4 text-slate-400 shrink-0" />
              </button>
            </div>

            {/* Input para digitação de Outro Sintoma */}
            {isCustomInputOpen && (
              <form onSubmit={handleAddCustomSymptom} className="p-3.5 bg-blue-50/80 border border-blue-200 rounded-2xl space-y-2 animate-in fade-in">
                <label className="text-xs font-bold text-blue-900 block">
                  Qual outro sintoma associado você está sentindo?
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customSymptomInput}
                    onChange={(e) => setCustomSymptomInput(e.target.value)}
                    placeholder="Ex: zumbido no ouvido, dor no olho, boca amarga..."
                    className="flex-1 px-3 py-2 text-xs rounded-xl border border-blue-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary"
                    autoFocus
                  />
                  <Button
                    type="submit"
                    size="sm"
                    disabled={!customSymptomInput.trim()}
                    className="rounded-xl px-4 text-xs font-bold shrink-0"
                  >
                    Adicionar
                  </Button>
                </div>
              </form>
            )}

            {/* Chips dos sintomas personalizados adicionados */}
            {selectedSecondary.filter((s) => !['Nenhum outro sintoma', ...((selectedComplaint?.id && SECONDARY_SYMPTOMS_BY_COMPLAINT[selectedComplaint.id]) || DEFAULT_SECONDARY_SYMPTOMS)].includes(s)).length > 0 && (
              <div className="pt-2 flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-semibold text-slate-500 mr-1">Outros sintomas adicionados:</span>
                {selectedSecondary.filter((s) => !['Nenhum outro sintoma', ...((selectedComplaint?.id && SECONDARY_SYMPTOMS_BY_COMPLAINT[selectedComplaint.id]) || DEFAULT_SECONDARY_SYMPTOMS)].includes(s)).map((customS) => (
                  <span
                    key={customS}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200 shadow-2xs"
                  >
                    <span>{customS}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveCustomSymptom(customS)}
                      className="p-0.5 hover:bg-blue-200 rounded-full cursor-pointer"
                      title="Remover sintoma"
                    >
                      <X className="w-3 h-3 text-blue-700" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ETAPA 3: Duração e Intensidade da Dor/Sintoma */}
        {step === 3 && (
          <div className="space-y-5 animate-in fade-in duration-200">
            <div>
              <h4 className="text-base font-bold text-slate-900">Há quanto tempo esse problema começou?</h4>
              <p className="text-xs text-muted-foreground mt-0.5">Selecione o tempo de evolução:</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {DURATION_OPTIONS.map((opt) => {
                const isSelected = selectedDuration === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setSelectedDuration(opt.id)}
                    className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                      isSelected
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20 shadow-xs'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="text-xs font-bold text-slate-900">{opt.label}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{opt.sub}</div>
                  </button>
                );
              })}
            </div>

            {/* Escala Visual de Intensidade */}
            <div className="pt-3 border-t border-slate-100 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-900">Qual a intensidade do desconforto agora?</span>
                <span className="text-xs font-extrabold px-2.5 py-0.5 rounded-full bg-slate-900 text-white">
                  Grau {intensity} / 10
                </span>
              </div>
              <div className="grid grid-cols-5 gap-1.5">
                {INTENSITY_LEVELS.map((lvl) => (
                  <button
                    key={lvl.val}
                    type="button"
                    onClick={() => setIntensity(lvl.val)}
                    className={`p-2 rounded-xl border text-center transition-all cursor-pointer ${
                      intensity === lvl.val
                        ? 'bg-primary text-white border-primary shadow-xs'
                        : 'bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-700'
                    }`}
                  >
                    <div className="text-sm font-black">{lvl.val}</div>
                    <div className="text-[9px] truncate">{lvl.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ETAPA 4: Medicamentos e Alergias Conhecidas */}
        {step === 4 && (
          <div className="space-y-4 animate-in fade-in duration-200">
            <div>
              <h4 className="text-base font-bold text-slate-900">Informações de Segurança do Paciente</h4>
              <p className="text-xs text-muted-foreground mt-0.5">Para a segurança da receita e conduta médica:</p>
            </div>

            {/* Medicamentos */}
            <div className="p-3.5 rounded-2xl border border-slate-200 bg-slate-50/50 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-900">
                  <Pill className="w-4 h-4 text-primary" />
                  <span>Você toma algum remédio de uso contínuo?</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setTakesMedication('nao')}
                    className={`px-3 py-1 text-xs rounded-lg font-semibold transition-all ${
                      takesMedication === 'nao' ? 'bg-slate-900 text-white' : 'bg-white border text-slate-700'
                    }`}
                  >
                    Não
                  </button>
                  <button
                    type="button"
                    onClick={() => setTakesMedication('sim')}
                    className={`px-3 py-1 text-xs rounded-lg font-semibold transition-all ${
                      takesMedication === 'sim' ? 'bg-primary text-white' : 'bg-white border text-slate-700'
                    }`}
                  >
                    Sim
                  </button>
                </div>
              </div>
              {takesMedication === 'sim' && (
                <input
                  type="text"
                  placeholder="Quais medicamentos? Ex: Losartana 50mg, Insulina..."
                  value={medicationDetails}
                  onChange={(e) => setMedicationDetails(e.target.value)}
                  className="w-full text-xs p-2 rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                />
              )}
            </div>

            {/* Alergias */}
            <div className="p-3.5 rounded-2xl border border-slate-200 bg-slate-50/50 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-900">
                  <AlertCircle className="w-4 h-4 text-rose-600" />
                  <span>Você possui alergia a algum medicamento?</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setHasAllergy('nao')}
                    className={`px-3 py-1 text-xs rounded-lg font-semibold transition-all ${
                      hasAllergy === 'nao' ? 'bg-slate-900 text-white' : 'bg-white border text-slate-700'
                    }`}
                  >
                    Não
                  </button>
                  <button
                    type="button"
                    onClick={() => setHasAllergy('sim')}
                    className={`px-3 py-1 text-xs rounded-lg font-semibold transition-all ${
                      hasAllergy === 'sim' ? 'bg-rose-600 text-white' : 'bg-white border text-slate-700'
                    }`}
                  >
                    Sim
                  </button>
                </div>
              </div>
              {hasAllergy === 'sim' && (
                <input
                  type="text"
                  placeholder="Qual medicamento? Ex: Dipirona, Penicilina..."
                  value={allergyDetails}
                  onChange={(e) => setAllergyDetails(e.target.value)}
                  className="w-full text-xs p-2 rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-rose-500"
                />
              )}
            </div>
          </div>
        )}

        {/* ETAPA 5: Resumo Clínico Estruturado Pré-Consulta */}
        {step === 5 && finalResult && (
          <div className="space-y-4 animate-in fade-in duration-200">
            <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
              <div>
                <h4 className="font-bold text-sm text-emerald-900">Triagem Concluída com Sucesso</h4>
                <p className="text-xs text-emerald-700">Seu prontuário pré-consulta foi estruturado e já está visível para o médico plantonista.</p>
              </div>
            </div>

            <div className="p-4 rounded-2xl border border-slate-200 bg-white space-y-2.5 text-xs text-slate-800">
              <div className="flex justify-between border-b pb-2">
                <span className="text-muted-foreground">Classificação de Risco:</span>
                <span className="font-bold text-slate-900 uppercase">{finalResult.riskClassification}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-muted-foreground">Queixa Principal:</span>
                <span className="font-semibold text-slate-900">{finalResult.chiefComplaint}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-muted-foreground">Evolução:</span>
                <span className="text-slate-900">{finalResult.symptomDuration}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-muted-foreground">Intensidade:</span>
                <span className="text-slate-900">{finalResult.symptomIntensity}/10</span>
              </div>
              <div>
                <span className="text-muted-foreground block mb-1">Resumo para o Médico:</span>
                <p className="p-2.5 rounded-lg bg-slate-50 border border-slate-200 leading-relaxed font-medium">
                  {finalResult.summary}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer com Ações */}
      <div className="p-4 border-t bg-slate-50/80 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors"
        >
          Cancelar
        </button>

        <div className="flex items-center gap-2">
          {step < 4 ? (
            <Button
              type="button"
              onClick={() => {
                if (step === 1 && !selectedComplaint) return;
                if (step === 3 && !selectedDuration) return;
                setStep((prev) => prev + 1);
              }}
              disabled={
                (step === 1 && !selectedComplaint) ||
                (step === 3 && !selectedDuration)
              }
              className="rounded-xl px-5 text-xs font-bold gap-1"
            >
              <span>Continuar</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          ) : step === 4 ? (
            <Button
              type="button"
              onClick={handleFinishQuestionnaire}
              disabled={takesMedication === null || hasAllergy === null || isSubmitting}
              className="rounded-xl px-6 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 shadow-sm"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>{isSubmitting ? 'Gerando Prontuário...' : 'Finalizar Triagem'}</span>
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => onComplete?.(finalResult!)}
              className="rounded-xl px-6 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 shadow-sm"
            >
              <span>Ir para a Fila do Plantão</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
